export function parseTimestampToMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    const dt = value.toDate();
    return Number.isFinite(dt?.getTime?.()) ? dt.getTime() : 0;
  }
  if (typeof value?.seconds === "number") {
    return value.seconds * 1000;
  }
  const asDate = new Date(value);
  const ms = asDate.getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function normalizeStatus(value, fallback = "") {
  return String(value ?? fallback).trim().toLowerCase();
}

function includesAny(status, tokens) {
  if (!status) return false;
  return tokens.some((token) => status.includes(token));
}

function isCompletedState(status) {
  return (
    ["completed", "complete", "received", "delivered", "done", "closed"].includes(status) ||
    includesAny(status, ["complet", "receiv", "deliver", "done", "closed"])
  );
}

function isSentState(status) {
  return (
    ["sent", "shipped", "on_way", "on-the-way", "in_transit", "in-transit"].includes(status) ||
    includesAny(status, ["sent", "ship", "transit", "on_way", "on-way"])
  );
}

function isCancelledState(status) {
  return (
    ["declined", "cancelled", "canceled", "expired", "rejected"].includes(status) ||
    includesAny(status, ["declin", "cancel", "expire", "reject"])
  );
}

function isAcceptedState(status) {
  return (
    ["accepted", "deal_accepted", "approved"].includes(status) ||
    includesAny(status, ["accept", "approv"])
  );
}

export function deriveLifecycleStatus(offer) {
  const baseStatus = normalizeStatus(offer?.status, "pending");
  const dealStatus = normalizeStatus(offer?.dealStatus);
  const sent = Boolean(offer?.deliverySentAt || offer?.chatDeal?.sentAt || offer?.chatDeal?.sent);
  const received = Boolean(
    offer?.deliveryReceivedAt || offer?.chatDeal?.receivedAt || offer?.chatDeal?.received
  );

  if (received || isCompletedState(dealStatus) || isCompletedState(baseStatus)) return "completed";
  if (sent || isSentState(dealStatus) || isSentState(baseStatus)) return "in-progress";

  if (isCancelledState(baseStatus) || isCancelledState(dealStatus)) return "cancelled";
  return "in-progress";
}

export function resolveProductImage(offer) {
  const p = offer?.product || {};
  if (Array.isArray(p.images) && p.images.length > 0) {
    const idx = Number.isInteger(p.coverIndex) ? p.coverIndex : 0;
    return p.images[idx] || p.images[0] || "/images/placeholder.jpg";
  }

  return (
    p.image ||
    p.photo ||
    p.thumbnail ||
    offer?.productImage ||
    offer?.metadata?.productImage ||
    "/images/placeholder.jpg"
  );
}

export function deriveOrderMessageKey(offer, isSeller) {
  const baseStatus = normalizeStatus(offer?.status, "pending");
  const dealStatus = normalizeStatus(offer?.dealStatus);
  const sent = Boolean(offer?.deliverySentAt || offer?.chatDeal?.sentAt || offer?.chatDeal?.sent);
  const received = Boolean(
    offer?.deliveryReceivedAt || offer?.chatDeal?.receivedAt || offer?.chatDeal?.received
  );

  if (received || isCompletedState(dealStatus) || isCompletedState(baseStatus)) {
    return "messages.completed";
  }

  if (isCancelledState(baseStatus) || isCancelledState(dealStatus)) {
    return "messages.cancelled";
  }

  if (sent || isSentState(dealStatus) || isSentState(baseStatus)) {
    return "messages.sentOnWay";
  }

  if (isAcceptedState(baseStatus) || isAcceptedState(dealStatus)) {
    return "messages.acceptedAwaitingShipment";
  }

  if (baseStatus !== "pending") {
    // Unknown non-pending states should still show concrete accepted flow text.
    return "messages.acceptedAwaitingShipment";
  }

  if (baseStatus === "pending") {
    return isSeller ? "messages.pendingSeller" : "messages.pendingBuyer";
  }

  return "messages.inProgress";
}

export function deriveOrderMessageOptions({
  offer,
  isSeller,
  partnerName,
  selfName,
}) {
  const baseStatus = normalizeStatus(offer?.status, "pending");
  const dealStatus = normalizeStatus(offer?.dealStatus);
  const sent = Boolean(offer?.deliverySentAt || offer?.chatDeal?.sentAt || offer?.chatDeal?.sent);
  const received = Boolean(
    offer?.deliveryReceivedAt || offer?.chatDeal?.receivedAt || offer?.chatDeal?.received
  );

  if (received || isCompletedState(dealStatus) || isCompletedState(baseStatus)) {
    return {};
  }

  if (isCancelledState(baseStatus) || isCancelledState(dealStatus)) {
    return {};
  }

  if (sent || isSentState(dealStatus) || isSentState(baseStatus)) {
    return {};
  }

  if (isAcceptedState(baseStatus) || isAcceptedState(dealStatus)) {
    return { name: isSeller ? (selfName || partnerName) : partnerName };
  }

  if (baseStatus !== "pending") {
    return { name: isSeller ? (selfName || partnerName) : partnerName };
  }

  return {};
}

export function formatAmountRwf(amount) {
  if (amount === null || amount === undefined || amount === "") return "-";
  const num = Number(amount);
  if (Number.isFinite(num)) return `${num.toLocaleString()} RWF`;
  return `${amount} RWF`;
}

export function resolveAcceptedOfferAmount(offer) {
  const candidates = [
    offer?.acceptedAmount,
    offer?.negotiatedAmount,
    offer?.finalAmount,
    offer?.offerAmount,
    offer?.amount,
    offer?.metadata?.acceptedAmount,
    offer?.metadata?.offerAmount,
    offer?.chatDeal?.amount,
  ];

  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    const asNum = Number(value);
    if (Number.isFinite(asNum)) return asNum;
    return value;
  }

  return null;
}
